import { ethers } from 'ethers';
import transactionService from './transactionService';

/**
 * Service for interacting with the Protocol Treasury contract
 */
class ProtocolTreasuryService {
  /**
   * Create a new ProtocolTreasuryService instance
   * @param {Object} contract - Ethers.js contract instance
   * @param {Object} provider - Ethers.js provider or signer
   */
  constructor(contract, provider) {
    this.contract = contract;
    this.provider = provider;
    
    // Initialize logging
    this.logPrefix = '[ProtocolTreasuryService]';
    console.log(`${this.logPrefix} Initialized with contract at ${contract.address}`);
  }

  /**
   * Helper method for logging errors with context
   * @private
   * @param {string} method - Method name where error occurred
   * @param {Error} error - The error object
   */
  _logError(method, error) {
    console.error(`${this.logPrefix} Error in ${method}:`, error);
    if (error.code) console.error(`${this.logPrefix} Error code:`, error.code);
    if (error.reason) console.error(`${this.logPrefix} Error reason:`, error.reason);
    if (error.message) console.error(`${this.logPrefix} Error message:`, error.message);
    if (error.data) console.error(`${this.logPrefix} Error data:`, error.data);
  }

  /**
   * Get the current CEL token balance of the treasury
   * @returns {Promise<string>} - The balance formatted as a string
   */
  async getBalance() {
    try {
      const balance = await this.contract.getBalance();
      return ethers.utils.formatEther(balance);
    } catch (error) {
      this._logError('getBalance', error);
      throw error;
    }
  }

  /**
   * Get the total accumulated fees
   * @returns {Promise<string>} - The total fees formatted as a string
   */
  async getTotalFees() {
    try {
      const totalFees = await this.contract.totalFees();
      return ethers.utils.formatEther(totalFees);
    } catch (error) {
      this._logError('getTotalFees', error);
      throw error;
    }
  }

  /**
   * Send fees from the treasury to a recipient (admin only)
   * @param {string} recipient - Recipient address
   * @param {string} amount - Amount to send in CEL tokens
   * @returns {Promise<Object>} - Transaction result
   */
  async sendFees(recipient, amount) {
    try {
      const amountInWei = ethers.utils.parseEther(amount.toString());
      const tx = this.contract.sendFees(recipient, amountInWei);
      
      return await transactionService.sendTransaction(tx, {
        title: 'Send Treasury Fees',
        description: `Sending ${amount} CEL to ${recipient}`,
        type: 'treasury_send_fees'
      });
    } catch (error) {
      this._logError('sendFees', error);
      throw error;
    }
  }

  /**
   * Emergency withdraw tokens from the treasury (admin only)
   * @param {string} tokenAddress - Token address (use address(0) for ETH)
   * @param {string} recipient - Recipient address
   * @param {string} amount - Amount to withdraw
   * @returns {Promise<Object>} - Transaction result
   */
  async emergencyWithdraw(tokenAddress, recipient, amount) {
    try {
      const amountInWei = ethers.utils.parseEther(amount.toString());
      const tx = this.contract.emergencyWithdraw(tokenAddress, recipient, amountInWei);
      
      return await transactionService.sendTransaction(tx, {
        title: 'Emergency Withdrawal',
        description: `Withdrawing ${amount} tokens to ${recipient}`,
        type: 'treasury_emergency_withdraw'
      });
    } catch (error) {
      this._logError('emergencyWithdraw', error);
      throw error;
    }
  }
}

export default ProtocolTreasuryService; 